<template>
  <div class="qr-profile-page">
    <div class="qr-page-inner">
      <!-- Loading -->
      <div v-if="loading" class="qr-card qr-state">
        <div class="qr-spinner"></div>
      </div>

      <!-- Not found / invalid token -->
      <div v-else-if="!student" class="qr-card qr-state">
        <i class="pi pi-times-circle qr-state-icon qr-state-icon--error"></i>
        <h2>لم يتم العثور على الطالب</h2>
        <p>رمز QR غير صالح أو منتهي الصلاحية.</p>
        <router-link to="/" class="btn-primary">الرئيسية</router-link>
      </div>

      <!-- Student dashboard -->
      <template v-else>
        <!-- ══════════ Header ══════════ -->
        <section class="qr-card qr-header-card">
          <div class="qr-avatar"><i class="pi pi-user"></i></div>
          <h1 class="qr-name">{{ student.name }}</h1>
          <div class="qr-row">
            <span class="qr-label">رقم الحجز</span>
            <span class="qr-value">{{ student.reservation_number }}</span>
          </div>
          <div class="qr-row" v-if="student.academic_year_name">
            <span class="qr-label">الصف الدراسي</span>
            <span class="qr-value">{{ student.academic_year_name }}</span>
          </div>
          <div class="qr-row" v-if="student.education_type">
            <span class="qr-label">نوع التعليم</span>
            <span class="qr-value">{{ student.education_type }}</span>
          </div>
          <div class="qr-row" v-if="studyModeLabel">
            <span class="qr-label">نوع الطالب</span>
            <span class="qr-value">{{ studyModeLabel }}</span>
          </div>
          <div class="qr-row" v-if="student.status_label">
            <span class="qr-label">حالة الحجز</span>
            <span class="qr-value">{{ student.status_label }}</span>
          </div>
        </section>

        <!-- ══════════ Attendance ══════════ -->
        <section class="qr-card qr-section">
          <div class="qr-section-head">
            <h2><i class="pi pi-calendar-check"></i> الحضور والغياب</h2>
            <select
              v-if="attendance"
              class="qr-select"
              :value="attendance.month"
              @change="onMonthChange($event.target.value)"
            >
              <option v-for="ym in attendance.available_months" :key="ym" :value="ym">
                {{ monthLabel(ym) }}
              </option>
            </select>
          </div>

          <div v-if="attendanceLoading" class="qr-inline-spinner"><div class="qr-spinner qr-spinner--sm"></div></div>
          <p v-else-if="attendanceError" class="qr-error">{{ attendanceError }}</p>
          <template v-else-if="attendance">
            <div class="qr-stats-row">
              <div class="qr-stat qr-stat--present">
                <span class="qr-stat-num">{{ attendance.summary.present }}</span>
                <span class="qr-stat-label">أيام الحضور</span>
              </div>
              <div class="qr-stat qr-stat--absent">
                <span class="qr-stat-num">{{ attendance.summary.absent }}</span>
                <span class="qr-stat-label">أيام الغياب</span>
              </div>
              <div class="qr-stat qr-stat--rate">
                <span class="qr-stat-num">{{ attendance.summary.percentage }}%</span>
                <span class="qr-stat-label">نسبة الحضور</span>
              </div>
            </div>

            <div class="qr-list qr-attendance-list">
              <div v-if="!attendance.records.length" class="qr-empty">
                لا توجد أيام حضور مجدولة لهذا الشهر.
              </div>
              <div
                v-for="rec in attendance.records"
                :key="rec.date"
                class="qr-list-row"
                :class="`qr-att--${rec.status}`"
              >
                <span class="qr-att-date">
                  <span class="qr-att-day">{{ rec.day_name }}</span>
                  <span class="qr-att-date-num">{{ shortDate(rec.date) }}</span>
                </span>
                <span class="qr-att-status">
                  <template v-if="rec.status === 'present'">✓ حاضر</template>
                  <template v-else-if="rec.status === 'absent'">✕ غائب</template>
                  <template v-else>— لم يسجل</template>
                </span>
              </div>
            </div>
          </template>
        </section>

        <!-- ══════════ Payments ══════════ -->
        <section class="qr-card qr-section">
          <div class="qr-section-head">
            <h2><i class="pi pi-wallet"></i> المدفوعات</h2>
          </div>

          <div v-if="paymentsLoading" class="qr-inline-spinner"><div class="qr-spinner qr-spinner--sm"></div></div>
          <p v-else-if="paymentsError" class="qr-error">{{ paymentsError }}</p>
          <template v-else-if="payments">
            <div class="qr-stats-row">
              <div class="qr-stat qr-stat--present">
                <span class="qr-stat-num">{{ formatAmount(payments.summary.paid) }}</span>
                <span class="qr-stat-label">إجمالي المدفوع</span>
              </div>
              <div class="qr-stat qr-stat--rate">
                <span class="qr-stat-num">{{ formatAmount(payments.summary.required) }}</span>
                <span class="qr-stat-label">المطلوب</span>
              </div>
              <div class="qr-stat qr-stat--absent">
                <span class="qr-stat-num">{{ formatAmount(payments.summary.remaining) }}</span>
                <span class="qr-stat-label">المتبقي</span>
              </div>
            </div>

            <div class="qr-list">
              <div v-if="!payments.items.length" class="qr-empty">لا توجد بنود مالية لهذا العام الدراسي.</div>
              <div v-for="item in payments.items" :key="item.id" class="qr-list-row">
                <span class="qr-payment-title">
                  {{ item.title }}
                  <span class="qr-payment-amount">{{ formatAmount(item.amount) }} ج.م</span>
                  <span v-if="item.paid_at" class="qr-payment-date">{{ shortDate(item.paid_at) }}</span>
                </span>
                <span class="qr-badge" :class="item.paid ? 'qr-badge--paid' : 'qr-badge--unpaid'">
                  {{ item.status_label }}
                </span>
              </div>
            </div>
          </template>
        </section>

        <!-- ══════════ Exam Results ══════════ -->
        <section class="qr-card qr-section">
          <div class="qr-section-head">
            <h2><i class="pi pi-file-edit"></i> نتائج الامتحانات</h2>
            <select
              v-if="exams && exams.available_years.length > 1"
              class="qr-select"
              :value="exams.academic_year_id"
              @change="onYearChange($event.target.value)"
            >
              <option v-for="y in exams.available_years" :key="y.id" :value="y.id">{{ y.name }}</option>
            </select>
          </div>

          <div v-if="examsLoading" class="qr-inline-spinner"><div class="qr-spinner qr-spinner--sm"></div></div>
          <p v-else-if="examsError" class="qr-error">{{ examsError }}</p>
          <template v-else-if="exams">
            <div class="qr-list">
              <div v-if="!exams.exams.length" class="qr-empty">لا توجد امتحانات لهذا العام الدراسي.</div>
              <div v-for="exam in exams.exams" :key="exam.exam_id" class="qr-exam-card">
                <div class="qr-exam-top">
                  <span class="qr-exam-title">{{ exam.title }}</span>
                  <span v-if="exam.date" class="qr-exam-date">{{ shortDate(exam.date) }}</span>
                </div>
                <template v-if="exam.taken">
                  <p v-if="exam.results_available === false">النتيجة غير متاحة للعرض حالياً.</p>
                  <div v-else class="qr-exam-score">
                    <span class="qr-exam-score-num">{{ exam.score }} / {{ exam.max_score }}</span>
                    <span class="qr-exam-percentage">{{ exam.percentage }}%</span>
                  </div>
                  <span class="qr-badge" :class="exam.status === 'timeout' ? 'qr-badge--unpaid' : 'qr-badge--paid'">
                    {{ exam.status_label }}
                  </span>
                </template>
                <span v-else class="qr-badge qr-badge--muted">{{ exam.status_label }}</span>
              </div>
            </div>
          </template>
        </section>

        <!-- ══════════ Periodic Follow-ups (read-only) ══════════ -->
        <section class="qr-card qr-section">
          <div class="qr-section-head">
            <h2><i class="pi pi-star"></i> المتابعات الدورية</h2>
          </div>

          <div v-if="followUpsLoading" class="qr-inline-spinner"><div class="qr-spinner qr-spinner--sm"></div></div>
          <p v-else-if="followUpsError" class="qr-error">{{ followUpsError }}</p>
          <template v-else-if="followUps">
            <div v-if="followUps.rating_summary.rated_count" class="qr-rating-summary">
              <div class="qr-rating-overall">
                <span class="qr-rating-overall-label">متوسط التقييم</span>
                <span class="qr-stars" aria-hidden="true">
                  <i v-for="n in 5" :key="n" class="pi" :class="starIcon(followUps.rating_summary.average_rating, n)"></i>
                </span>
                <strong dir="ltr">{{ followUps.rating_summary.average_rating }} / 5</strong>
              </div>

              <div v-if="followUps.rating_summary.by_type.length" class="qr-rating-by-type">
                <div v-for="t in followUps.rating_summary.by_type" :key="t.follow_up_type_id" class="qr-rating-by-type-row">
                  <span class="qr-rating-type-name">{{ t.name }}</span>
                  <span class="qr-stars" aria-hidden="true">
                    <i v-for="n in 5" :key="n" class="pi" :class="starIcon(t.average_rating, n)"></i>
                  </span>
                  <strong dir="ltr">{{ t.average_rating }} / 5</strong>
                </div>
              </div>
            </div>

            <div class="qr-list">
              <div v-if="!followUps.follow_ups.length" class="qr-empty">لا توجد متابعات دورية حتى الآن.</div>
              <div v-for="f in followUps.follow_ups" :key="f.id" class="qr-followup-card">
                <div class="qr-followup-top">
                  <span class="qr-badge qr-badge--muted">{{ f.type }}</span>
                  <span v-if="f.date" class="qr-exam-date">{{ shortDate(f.date) }}</span>
                </div>
                <p class="qr-followup-content">{{ f.content }}</p>
                <span class="qr-stars" :aria-label="`${f.rating} من 5 نجوم`">
                  <i v-for="n in 5" :key="n" class="pi" :class="starIcon(f.rating, n)"></i>
                </span>
              </div>
            </div>
          </template>
        </section>
      </template>
    </div>
  </div>
</template>

<script>
import { qrService } from '@/services/qrService.js';

const STUDY_MODE_LABELS = { online: 'أونلاين', offline: 'أوفلاين', hybrid: 'أوفلاين + أونلاين' };
const ARABIC_MONTHS = [
  'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
  'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر',
];

export default {
  name: 'QrProfile',

  data() {
    return {
      token: '',
      loading: true,
      student: null,

      attendance: null,
      attendanceLoading: false,
      attendanceError: '',

      payments: null,
      paymentsLoading: false,
      paymentsError: '',

      exams: null,
      examsLoading: false,
      examsError: '',

      followUps: null,
      followUpsLoading: false,
      followUpsError: '',
    };
  },

  computed: {
    studyModeLabel() {
      return STUDY_MODE_LABELS[this.student?.study_mode] || '';
    },
  },

  methods: {
    monthLabel(ym) {
      const [y, m] = ym.split('-').map(Number);
      return `${ARABIC_MONTHS[m - 1]} ${y}`;
    },

    shortDate(dateStr) {
      const [y, m, d] = dateStr.split('-').map(Number);
      return `${d} ${ARABIC_MONTHS[m - 1]} ${y}`;
    },

    formatAmount(n) {
      return new Intl.NumberFormat('ar-EG').format(n ?? 0);
    },

    // Same fill/half/empty star rule as the admin follow-up rating widget
    // (FollowUpRating.vue): whole stars up to the value, one half-star step,
    // the rest empty. Read-only here — this page never emits a rating.
    starIcon(value, star) {
      if (value == null) return 'pi-star';
      if (star <= value) return 'pi-star-fill';
      if (star - 0.5 <= value) return 'pi-star-half-fill';
      return 'pi-star';
    },

    async loadAttendance(month) {
      this.attendanceLoading = true;
      this.attendanceError = '';
      try {
        const res = await qrService.attendance(this.token, month);
        this.attendance = res?.status ? res.data : null;
        if (!this.attendance) this.attendanceError = 'تعذّر تحميل بيانات الحضور';
      } catch {
        this.attendanceError = 'تعذّر تحميل بيانات الحضور';
      } finally {
        this.attendanceLoading = false;
      }
    },

    async loadPayments() {
      this.paymentsLoading = true;
      this.paymentsError = '';
      try {
        const res = await qrService.payments(this.token);
        this.payments = res?.status ? res.data : null;
        if (!this.payments) this.paymentsError = 'تعذّر تحميل بيانات المدفوعات';
      } catch {
        this.paymentsError = 'تعذّر تحميل بيانات المدفوعات';
      } finally {
        this.paymentsLoading = false;
      }
    },

    async loadExams(academicYearId) {
      this.examsLoading = true;
      this.examsError = '';
      try {
        const res = await qrService.exams(this.token, academicYearId);
        this.exams = res?.status ? res.data : null;
        if (!this.exams) this.examsError = 'تعذّر تحميل نتائج الامتحانات';
      } catch {
        this.examsError = 'تعذّر تحميل نتائج الامتحانات';
      } finally {
        this.examsLoading = false;
      }
    },

    async loadFollowUps() {
      this.followUpsLoading = true;
      this.followUpsError = '';
      try {
        const res = await qrService.followUps(this.token);
        this.followUps = res?.status ? res.data : null;
        if (!this.followUps) this.followUpsError = 'تعذّر تحميل المتابعات الدورية';
      } catch {
        this.followUpsError = 'تعذّر تحميل المتابعات الدورية';
      } finally {
        this.followUpsLoading = false;
      }
    },

    onMonthChange(month) {
      this.loadAttendance(month);
    },

    onYearChange(academicYearId) {
      this.loadExams(academicYearId);
    },
  },

  async mounted() {
    this.token = this.$route.params.token;
    try {
      const res = await qrService.resolve(this.token);
      this.student = res?.status ? res.data : null;
    } catch {
      this.student = null;
    } finally {
      this.loading = false;
    }

    if (this.student) {
      // Priority: profile first (already shown), then attendance/payments/exams/
      // follow-ups load independently in parallel — one section's slow/failed
      // request never blocks the others.
      this.loadAttendance();
      this.loadPayments();
      this.loadExams();
      this.loadFollowUps();
    }
  },
};
</script>

<style scoped>
* , *::before, *::after { box-sizing: border-box; }

.qr-profile-page {
  min-height: 100vh;
  padding: 1.25rem 0.875rem 3rem;
  font-family: 'Cairo', sans-serif;
  direction: rtl;
  background: linear-gradient(180deg, #F5F7FF 0%, #ffffff 65%);
}

.qr-page-inner {
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
}

.qr-card {
  background: #fff;
  border-radius: 20px;
  padding: 1.5rem 1.25rem;
  box-shadow: 0 10px 30px rgba(17,24,39,.08);
  border: 1px solid rgba(91,95,239,.08);
}

.qr-state {
  display: flex; flex-direction: column; align-items: center; gap: 0.75rem;
  text-align: center; margin-top: 15vh;
}
.qr-state h2 { font-size: 1.15rem; font-weight: 800; color: #111827; margin: 0.25rem 0 0; }
.qr-state p { color: #6B7280; margin: 0 0 0.75rem; }
.qr-state-icon { font-size: 2.6rem; color: #94a3b8; }
.qr-state-icon--error { color: #ef4444; }

.qr-spinner {
  width: 42px; height: 42px;
  border: 4px solid rgba(91,95,239,.15);
  border-top-color: #5B5FEF;
  border-radius: 50%;
  animation: qr-spin 0.8s linear infinite;
}
.qr-spinner--sm { width: 26px; height: 26px; border-width: 3px; }
.qr-inline-spinner { display: flex; justify-content: center; padding: 1rem 0; }
@keyframes qr-spin { to { transform: rotate(360deg); } }

.qr-error { color: #ef4444; font-size: 0.9rem; text-align: center; padding: 0.5rem 0; margin: 0; }
.qr-empty { color: #94a3b8; font-size: 0.9rem; text-align: center; padding: 1rem 0; }

/* ── Header card ── */
.qr-header-card { text-align: center; }
.qr-avatar {
  width: 64px; height: 64px;
  margin: 0 auto 0.85rem;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, rgba(91,95,239,.12), rgba(124,77,255,.12));
  color: #5B5FEF;
  font-size: 1.6rem;
}
.qr-name { font-size: 1.3rem; font-weight: 800; color: #111827; margin: 0 0 1rem; word-break: break-word; }
.qr-row {
  display: flex; align-items: center; justify-content: space-between;
  padding: 0.6rem 0; border-top: 1px solid #F1F2F6;
}
.qr-row:first-of-type { border-top: none; }
.qr-label { color: #6B7280; font-size: 0.88rem; }
.qr-value { color: #111827; font-weight: 700; font-size: 0.92rem; text-align: left; }

.btn-primary {
  display: inline-flex; align-items: center; justify-content: center;
  padding: .7rem 1.8rem; border-radius: 99px;
  font-weight: 700; font-size: .95rem; text-decoration: none;
  background: linear-gradient(135deg,#5B5FEF,#7C4DFF); color: #fff;
  box-shadow: 0 12px 32px rgba(91,95,239,.3);
}

/* ── Section (attendance/payments/exams) ── */
.qr-section-head {
  display: flex; align-items: center; justify-content: space-between;
  gap: 0.5rem; margin-bottom: 1rem; flex-wrap: wrap;
}
.qr-section-head h2 {
  font-size: 1.02rem; font-weight: 800; color: #111827; margin: 0;
  display: flex; align-items: center; gap: 0.4rem;
}
.qr-section-head h2 .pi { color: #5B5FEF; font-size: 1rem; }

.qr-select {
  border: 1px solid #E5E7EB; border-radius: 10px;
  padding: 0.5rem 0.75rem; font-family: inherit; font-size: 0.85rem;
  color: #111827; background: #F9FAFB;
  min-height: 40px; /* touch target */
  max-width: 100%;
}

/* ── Stat tiles ── */
.qr-stats-row {
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 0.6rem;
  margin-bottom: 1.1rem;
}
.qr-stat {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 0.2rem; padding: 0.85rem 0.4rem; border-radius: 14px;
  background: #F8F9FD;
}
.qr-stat-num { font-size: 1.35rem; font-weight: 900; line-height: 1; }
.qr-stat-label { font-size: 0.72rem; color: #6B7280; font-weight: 600; text-align: center; }
.qr-stat--present { background: rgba(16,185,129,.08); }
.qr-stat--present .qr-stat-num { color: #059669; }
.qr-stat--absent { background: rgba(239,68,68,.08); }
.qr-stat--absent .qr-stat-num { color: #dc2626; }
.qr-stat--rate { background: rgba(91,95,239,.08); }
.qr-stat--rate .qr-stat-num { color: #5B5FEF; }

/* ── Lists (mobile-friendly rows instead of tables) ── */
.qr-list { display: flex; flex-direction: column; gap: 0.5rem; }

.qr-attendance-list {
  max-height: 340px;
  overflow-y: auto;
  padding-inline-end: 2px;
}

.qr-list-row {
  display: flex; align-items: center; justify-content: space-between;
  gap: 0.5rem; padding: 0.65rem 0.75rem; border-radius: 12px;
  background: #F8F9FD; font-size: 0.85rem;
}

.qr-att-date { display: flex; flex-direction: column; gap: 0.1rem; }
.qr-att-day { font-weight: 700; color: #111827; }
.qr-att-date-num { font-size: 0.75rem; color: #9CA3AF; }
.qr-att-status { font-weight: 700; white-space: nowrap; }
.qr-att--present .qr-att-status { color: #059669; }
.qr-att--absent .qr-att-status { color: #dc2626; }
.qr-att--not_recorded .qr-att-status { color: #9CA3AF; }
.qr-att--not_recorded { opacity: 0.7; }

.qr-payment-title {
  display: flex; flex-direction: column; gap: 0.15rem;
  font-weight: 700; color: #111827; font-size: 0.88rem;
}
.qr-payment-amount { font-weight: 600; color: #6B7280; font-size: 0.78rem; }
.qr-payment-date { font-size: 0.72rem; color: #9CA3AF; }

.qr-badge {
  display: inline-flex; align-items: center; justify-content: center;
  padding: 0.3rem 0.75rem; border-radius: 99px;
  font-size: 0.75rem; font-weight: 700; white-space: nowrap;
}
.qr-badge--paid { background: rgba(16,185,129,.12); color: #059669; }
.qr-badge--unpaid { background: rgba(239,68,68,.12); color: #dc2626; }
.qr-badge--muted { background: #F1F2F6; color: #9CA3AF; }

/* ── Exam cards ── */
.qr-exam-card {
  display: flex; flex-direction: column; gap: 0.5rem;
  padding: 0.85rem 0.9rem; border-radius: 14px; background: #F8F9FD;
}
.qr-exam-top { display: flex; align-items: baseline; justify-content: space-between; gap: 0.5rem; }
.qr-exam-title { font-weight: 800; color: #111827; font-size: 0.92rem; }
.qr-exam-date { font-size: 0.75rem; color: #9CA3AF; white-space: nowrap; }
.qr-exam-score { display: flex; align-items: center; justify-content: space-between; }
.qr-exam-score-num { font-size: 1.15rem; font-weight: 900; color: #5B5FEF; }
.qr-exam-percentage { font-size: 0.85rem; font-weight: 700; color: #6B7280; }

/* ── Periodic follow-ups (read-only) ── */
.qr-stars { display: inline-flex; gap: 0.15rem; font-size: 0.95rem; color: #E5E7EB; }
.qr-stars .pi-star-fill, .qr-stars .pi-star-half-fill { color: #F59E0B; }

.qr-rating-summary {
  padding: 0.85rem 0.9rem; margin-bottom: 1rem; border-radius: 14px;
  background: #F8F9FD; border-inline-start: 3px solid #F59E0B;
}
.qr-rating-overall {
  display: flex; flex-wrap: wrap; align-items: center; gap: 0.4rem 0.6rem;
}
.qr-rating-overall-label { font-weight: 700; color: #111827; font-size: 0.85rem; }
.qr-rating-overall strong { font-size: 0.85rem; color: #6B7280; }
.qr-rating-by-type { margin-top: 0.6rem; padding-top: 0.6rem; border-top: 1px solid #EDEEF5; }
.qr-rating-by-type-row {
  display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between;
  gap: 0.3rem 0.6rem; padding: 0.35rem 0; font-size: 0.82rem;
}
.qr-rating-type-name { color: #111827; font-weight: 600; flex: 1 1 8rem; min-width: 0; }
.qr-rating-by-type-row strong { color: #6B7280; }

.qr-followup-card {
  display: flex; flex-direction: column; gap: 0.5rem;
  padding: 0.85rem 0.9rem; border-radius: 14px; background: #F8F9FD;
}
.qr-followup-top { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; }
.qr-followup-content { margin: 0; color: #374151; font-size: 0.88rem; line-height: 1.6; word-break: break-word; white-space: pre-wrap; }

/* ── 360 / 390 / 428px phones ── */
@media (max-width: 400px) {
  .qr-card { padding: 1.15rem 0.9rem; border-radius: 16px; }
  .qr-stat-num { font-size: 1.15rem; }
  .qr-name { font-size: 1.15rem; }
}
</style>
